import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PosDeviceContext } from '../context/PosDeviceContext';
import PageNavbar from './PageNavbar';
import AppIcon from './icons/AppIcon';
import api from '../config/api';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import './Dashboard.css';
import './HubShell.css';
import { formatDateDisplay } from '../utils/formatDate';

const SHARE_COLORS = ['#1a1a1a', '#b8860b', '#d4af37', '#5c1520', '#78716c', '#c9a227', '#3f3a34', '#a3823a'];

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n) || 0);

const formatInr2 = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(n) || 0);

const formatDayLong = (isoDate) => formatDateDisplay(isoDate || new Date());

const Trend = ({ delta, label }) => {
  if (!delta) return null;
  const dir = delta.direction || 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '●';
  const sign = delta.pct > 0 ? '+' : '';
  return (
    <span className={`dash-trend dash-trend--${dir}`} title={label || ''}>
      <span className="dash-trend__arrow" aria-hidden>
        {arrow}
      </span>
      {sign}
      {Math.abs(delta.pct)}%
      <em>{label}</em>
    </span>
  );
};

const KpiCard = ({
  to,
  label,
  value,
  meta,
  trend,
  chips,
  tone,
  cta,
  icon
}) => {
  const className = [
    'dash-kpi',
    'dash-kpi--link',
    tone === 'hero' ? 'dash-kpi--hero' : '',
    tone === 'warn' ? 'dash-kpi--warn' : '',
    tone === 'alert' ? 'dash-kpi--alert' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Link to={to} className={className} aria-label={`${label}: ${value}. ${cta || 'Open details'}`}>
      <div className="dash-kpi__top">
        <span className="dash-kpi__label">{label}</span>
        {icon ? (
          <span className="dash-kpi__icon" aria-hidden>
            <AppIcon name={icon} size={16} />
          </span>
        ) : null}
      </div>
      <strong className="dash-kpi__value">{value}</strong>
      {trend || null}
      {meta ? <span className="dash-kpi__meta">{meta}</span> : null}
      {chips?.length ? (
        <div className="dash-kpi__chips">
          {chips.map((c) => (
            <span key={c.text} className={c.danger ? 'is-danger' : undefined}>
              {c.text}
            </span>
          ))}
        </div>
      ) : null}
      <span className="dash-kpi__cta">
        {cta || 'View details'}
        <AppIcon name="chevron" size={14} />
      </span>
    </Link>
  );
};

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const { enrolled: posDeviceEnrolled } = useContext(PosDeviceContext);
  const [home, setHome] = useState(null);
  const [homeErr, setHomeErr] = useState('');
  const [homeLoading, setHomeLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isAdmin = user?.role === 'Admin';

  const loadHome = useCallback(async () => {
    if (!isAdmin) return;
    setHomeLoading(true);
    setHomeErr('');
    try {
      const { data } = await api.get('/owner/dashboard-home', { params: { bigOrderMin: 5000 } });
      setHome(data);
      setLastUpdated(new Date());
    } catch (e) {
      setHomeErr(e.response?.data?.error || e.message || 'Could not load dashboard');
      setHome(null);
    } finally {
      setHomeLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadHome();
  }, [loadHome]);

  const canCreateOrder = ['Order Creator', 'Admin', 'Cashier', 'Franchise Admin', 'Store Manager', 'Accountant'].includes(
    user?.role
  );
  const canViewStoreManager = ['Store Manager', 'Admin', 'Franchise Admin'].includes(user?.role);
  const canViewAccountant = ['Accountant', 'Admin'].includes(user?.role);
  const canViewAnalytics = user?.role === 'Admin';
  const canManageUsers = user?.role === 'Admin';
  const canManageOrderCatalog = ['Admin', 'Order Creator'].includes(user?.role);
  const canPosCrm =
    posDeviceEnrolled && ['Admin', 'Store Manager', 'Cashier', 'Franchise Admin'].includes(user?.role);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const todayIso = home?.dates?.today;
  const ordersTodayPath = todayIso ? `/orders?date=${todayIso}` : '/orders';
  const storeFocusPath = '/store-manager';

  const shareData = useMemo(() => {
    const slices = home?.revenueShare?.slices || [];
    return slices.map((s, i) => ({
      ...s,
      fill: SHARE_COLORS[i % SHARE_COLORS.length]
    }));
  }, [home]);

  const shareTotal = home?.revenueShare?.total || 0;
  const onlineSlice = shareData.find((s) => s.name === 'Online Orders');

  const flavourChart = useMemo(
    () =>
      (home?.productInsights?.topFlavours || []).map((r) => ({
        ...r,
        label: r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name
      })),
    [home]
  );
  const dessertChart = useMemo(
    () =>
      (home?.productInsights?.topDesserts || []).map((r) => ({
        ...r,
        label: r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name
      })),
    [home]
  );
  const agentChart = useMemo(
    () =>
      (home?.productInsights?.agentSales || []).map((r) => ({
        ...r,
        label: r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name
      })),
    [home]
  );

  const insightsPeriodLabel = useMemo(() => {
    const p = home?.productInsights?.period;
    if (!p?.start || !p?.end) return 'Last 7 days';
    if (p.start === p.end) return formatDateDisplay(p.start);
    return `${formatDateDisplay(p.start)} – ${formatDateDisplay(p.end)}`;
  }, [home]);

  const opsSummary = useMemo(() => {
    if (!home) return [];
    return [
      {
        label: 'Open today',
        value: home.todayDeliveries.pending,
        tone: home.todayDeliveries.pending > 0 ? 'warn' : 'ok'
      },
      {
        label: 'Overdue',
        value: home.pendingDeliveries.overdue,
        tone: home.pendingDeliveries.overdue > 0 ? 'danger' : 'ok'
      },
      {
        label: 'Unsettled',
        value: home.pendingDues.orderCount,
        tone: home.pendingDues.orderCount > 0 ? 'warn' : 'ok'
      },
      {
        label: 'Today revenue',
        value: formatInr(home.todayRevenue.amount),
        tone: 'neutral'
      }
    ];
  }, [home]);

  const cards = useMemo(
    () =>
      [
        {
          to: '/orders',
          icon: 'orders',
          title: 'Orders',
          description: 'OMS pipeline, filters, and order detail',
          available: true
        },
        {
          to: '/orders/create',
          icon: 'create',
          title: 'Create order',
          description: 'New delivery / store / catering order',
          available: canCreateOrder
        },
        {
          to: '/store-manager',
          icon: 'store',
          title: 'Store manager',
          description: 'Today, next day, and future fulfilment',
          available: canViewStoreManager
        },
        {
          to: '/pos',
          icon: 'pos',
          title: 'POS & CRM',
          description: 'Walk-in sales, leads, outlets',
          available: canPosCrm
        },
        {
          to: '/owner',
          icon: 'owner',
          title: 'Owner overview',
          description: 'Franchise snapshot: OMS + POS + CRM',
          available: user?.role === 'Admin'
        },
        {
          to: '/accountant',
          icon: 'accountant',
          title: 'Accountant',
          description: 'Payments and settlement',
          available: canViewAccountant
        },
        {
          to: '/analytics',
          icon: 'analytics',
          title: 'Business insights',
          description: 'Revenue trends, mix, and performance',
          available: canViewAnalytics
        },
        {
          to: '/users',
          icon: 'users',
          title: 'Users',
          description: 'Roles and outlet access',
          available: canManageUsers
        },
        {
          to: '/agents',
          icon: 'agents',
          title: 'Agents',
          description: 'Delivery partners',
          available: canManageOrderCatalog
        },
        {
          to: '/desserts',
          icon: 'desserts',
          title: 'Desserts',
          description: 'Catalogue and pricing',
          available: canManageOrderCatalog
        },
        {
          to: '/cup-cakes',
          icon: 'cupcakes',
          title: 'Cup cakes',
          description: 'Catalogue and pricing',
          available: canManageOrderCatalog
        },
        {
          to: '/plants',
          icon: 'plants',
          title: 'Plants',
          description: 'Catalogue and pricing',
          available: canManageOrderCatalog
        },
        {
          to: '/flavours',
          icon: 'flavours',
          title: 'Cake flavours',
          description: 'Flavour list',
          available: canManageOrderCatalog
        },
        {
          to: '/catering-menu',
          icon: 'catering',
          title: 'Menu items',
          description: 'Silver, Gold & Platinum catering plates',
          available: canManageOrderCatalog
        },
        {
          to: '/catering-planner',
          icon: 'planner',
          title: 'CaterCraft',
          description: 'Per-plate planner and bulk pricing',
          available: true
        }
      ].filter((card) => card.available),
    [
      user,
      canCreateOrder,
      canViewStoreManager,
      canViewAccountant,
      canViewAnalytics,
      canManageUsers,
      canManageOrderCatalog,
      canPosCrm
    ]
  );

  if (user?.role === 'Cashier' && posDeviceEnrolled) {
    return <Navigate to="/pos" replace />;
  }

  if (user?.role === 'Store Manager') {
    return <Navigate to="/store-manager" replace />;
  }

  return (
    <div className="dashboard">
      <PageNavbar />
      <div className="dashboard-container">
        {isAdmin ? (
          <header className="dash-ops-hero" aria-label="Admin operations header">
            <div className="dash-ops-hero__main">
              <div className="dash-ops-hero__eyebrow">
                <span className="dash-ops-hero__role">{user?.role}</span>
                <span className="dash-ops-hero__date">{formatDayLong(todayIso)}</span>
              </div>
              <h1 className="dash-ops-hero__title">Operations desk</h1>
              <p className="dash-ops-hero__lead">
                {user?.full_name || user?.username
                  ? `${getGreeting()}, ${user.full_name || user.username}. `
                  : ''}
                Live bakery pulse — revenue, deliveries, and dues that need action.
              </p>
              {opsSummary.length ? (
                <div className="dash-ops-hero__stats" aria-label="Today summary">
                  {opsSummary.map((s) => (
                    <div key={s.label} className={`dash-ops-stat dash-ops-stat--${s.tone}`}>
                      <em>{s.label}</em>
                      <strong>{s.value}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="dash-ops-hero__stats dash-ops-hero__stats--loading">
                  <span>Loading live metrics…</span>
                </div>
              )}
            </div>
            <div className="dash-ops-hero__actions">
              <Link to="/orders/create" className="btn btn-primary dash-ops-hero__btn">
                Create order
              </Link>
              <Link to={ordersTodayPath} className="btn btn-secondary dash-ops-hero__btn">
                Today&apos;s orders
              </Link>
              <Link to="/analytics?preset=today" className="btn btn-secondary dash-ops-hero__btn">
                Insights
              </Link>
              <button
                type="button"
                className="btn btn-secondary dash-ops-hero__btn"
                onClick={loadHome}
                disabled={homeLoading}
              >
                {homeLoading ? 'Refreshing…' : 'Refresh'}
              </button>
              {lastUpdated ? (
                <span className="dash-ops-hero__updated">
                  Updated{' '}
                  {lastUpdated.toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              ) : null}
            </div>
          </header>
        ) : (
          <div className="dashboard-hero dashboard-hero--compact dashboard-hero--light">
            <div className="hero-content hero-content--left">
              <h1 className="hero-title">
                {getGreeting()}, {user?.full_name || user?.username}
              </h1>
              <p className="hero-subtitle hero-subtitle--left">
                CakeXpress operations — orders, stores, and POS in one place.
              </p>
              <div className="hero-badge">
                <span className="badge-text">{user?.role}</span>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <section className="dash-home" aria-label="Admin command centre">
            <div className="dash-home__toolbar">
              <div>
                <h2 className="dash-home__title">Today at a glance</h2>
                <p className="dash-home__lead">
                  Tap any card to open the matching screen
                  {todayIso ? ` · IST ${todayIso}` : ''}
                </p>
              </div>
            </div>

            {homeErr ? <div className="dash-home__error">{homeErr}</div> : null}

            {homeLoading && !home ? (
              <div className="dash-home__kpis">
                {[1, 2, 3, 4, 5].map((k) => (
                  <div key={k} className="dash-kpi dash-kpi--skeleton" />
                ))}
              </div>
            ) : null}

            {home ? (
              <>
                <div className="dash-home__kpis">
                  <KpiCard
                    to="/analytics?preset=today"
                    tone="hero"
                    icon="analytics"
                    label="Today’s revenue"
                    value={formatInr(home.todayRevenue.amount)}
                    trend={<Trend delta={home.todayRevenue.vsYesterday} label="vs yesterday" />}
                    meta={`OMS ${formatInr(home.todayRevenue.oms)} (${home.todayRevenue.omsOrders || 0} orders) · POS ${formatInr(home.todayRevenue.pos)} (${home.todayRevenue.posTickets || 0} tickets)`}
                    cta="Open today’s insights"
                  />

                  <KpiCard
                    to="/analytics?preset=7d"
                    icon="owner"
                    label="This week’s revenue"
                    value={formatInr(home.weekRevenue.amount)}
                    trend={<Trend delta={home.weekRevenue.vsPreviousWeek} label="vs prior 7 days" />}
                    meta={`OMS ${formatInr(home.weekRevenue.oms)} · POS ${formatInr(home.weekRevenue.pos)} · ${home.dates.weekStart} → ${home.dates.weekEnd}`}
                    cta="Open 7-day insights"
                  />

                  <KpiCard
                    to="/accountant"
                    tone="warn"
                    icon="accountant"
                    label="Pending dues"
                    value={formatInr(home.pendingDues.amount)}
                    meta={`${home.pendingDues.orderCount} unsettled order${home.pendingDues.orderCount === 1 ? '' : 's'} awaiting collection`}
                    cta="Open accountant"
                  />

                  <KpiCard
                    to={ordersTodayPath}
                    icon="orders"
                    label="Today’s deliveries"
                    value={home.todayDeliveries.total}
                    meta={`${home.todayDeliveries.pending} still open · ${home.todayDeliveries.delivered} delivered`}
                    chips={[
                      { text: `Pending ${home.todayDeliveries.notStarted}` },
                      { text: `In progress ${home.todayDeliveries.inProgress}` },
                      { text: `Ready ${home.todayDeliveries.ready}` }
                    ]}
                    cta="View today’s orders"
                  />

                  <KpiCard
                    to={storeFocusPath}
                    tone={home.pendingDeliveries.overdue > 0 ? 'alert' : undefined}
                    icon="store"
                    label="Open pipeline"
                    value={home.pendingDeliveries.totalOpen}
                    meta="All open OMS deliveries across dates"
                    chips={[
                      { text: `Overdue ${home.pendingDeliveries.overdue}`, danger: home.pendingDeliveries.overdue > 0 },
                      { text: `Today ${home.pendingDeliveries.today}` },
                      { text: `Next 14d ${home.pendingDeliveries.upcoming}` }
                    ]}
                    cta="Open store manager"
                  />

                  <KpiCard
                    to="/orders?category=Events"
                    icon="planner"
                    label="Events done"
                    value={home.eventsDone?.count ?? 0}
                    meta="Delivered Events orders so far"
                    cta="View events orders"
                  />

                  <KpiCard
                    to="/orders?category=Events"
                    icon="catering"
                    label="Grazing tables served"
                    value={home.grazingTablesServed?.count ?? 0}
                    meta="Delivered orders with grazing table so far"
                    cta="View related orders"
                  />

                  <KpiCard
                    to="/expenses"
                    tone={home.expenses?.pendingCount > 0 ? 'warn' : undefined}
                    icon="accountant"
                    label="POS expenses pending"
                    value={home.expenses?.pendingCount ?? 0}
                    meta={`${formatInr(home.expenses?.pendingAmount || 0)} awaiting approval · ${formatInr(home.expenses?.monthApprovedAmount || 0)} approved this month`}
                    cta="Review expenses"
                  />

                  <KpiCard
                    to="/order-approvals"
                    tone={home.orderApprovals?.awaitingCount > 0 ? 'warn' : undefined}
                    icon="orders"
                    label="Orders awaiting approval"
                    value={home.orderApprovals?.awaitingCount ?? 0}
                    meta="Online Orders under ₹500"
                    cta="Review orders"
                  />
                </div>

                <div className="dash-home__grid">
                  <section className="dash-panel">
                    <div className="dash-panel__head dash-panel__head--row">
                      <div>
                        <h3>Store POS sales</h3>
                        <p>Today and this week by outlet</p>
                      </div>
                      <Link to="/pos" className="dash-panel__action">
                        Open POS →
                      </Link>
                    </div>
                    {home.posByStore?.length ? (
                      <div className="dash-store-list">
                        {home.posByStore.map((s) => (
                          <div key={s.id} className="dash-store-row">
                            <div>
                              <strong>{s.name}</strong>
                              <small>{s.code}</small>
                            </div>
                            <div className="dash-store-row__nums">
                              <div>
                                <em>Today</em>
                                <span>{formatInr(s.todayRevenue)}</span>
                                <small>{s.todayCount} tickets</small>
                              </div>
                              <div>
                                <em>Week</em>
                                <span>{formatInr(s.weekRevenue)}</span>
                                <small>{s.weekCount} tickets</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="dash-panel__empty">No POS store data yet.</div>
                    )}
                  </section>

                  <section className="dash-panel">
                    <div className="dash-panel__head dash-panel__head--row">
                      <div>
                        <h3>Revenue share (7 days)</h3>
                        <p>
                          Online vs stores / desserts / catering / POS
                          {onlineSlice ? ` · Online ${onlineSlice.pct}%` : ''}
                        </p>
                      </div>
                      <Link to="/analytics?preset=7d" className="dash-panel__action">
                        Full insights →
                      </Link>
                    </div>
                    {shareData.length && shareTotal > 0 ? (
                      <div className="dash-share">
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={shareData}
                              dataKey="revenue"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={52}
                              outerRadius={78}
                              paddingAngle={2}
                            >
                              {shareData.map((entry) => (
                                <Cell key={entry.name} fill={entry.fill} stroke="#fff" strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [formatInr2(value), name]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <ul className="dash-share__legend">
                          {shareData.map((s) => (
                            <li key={s.name}>
                              <i style={{ background: s.fill }} />
                              <span>{s.name}</span>
                              <strong>{s.pct}%</strong>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="dash-panel__empty">No revenue in the last 7 days.</div>
                    )}
                  </section>
                </div>

                <div className="dash-home__grid dash-home__grid--insights">
                  <section className="dash-panel">
                    <div className="dash-panel__head dash-panel__head--row">
                      <div>
                        <h3>Top cake flavours</h3>
                        <p>Highest revenue · {insightsPeriodLabel}</p>
                      </div>
                      <Link to="/analytics?preset=7d" className="dash-panel__action">
                        Full report →
                      </Link>
                    </div>
                    {flavourChart.length ? (
                      <div className="dash-rank-chart">
                        <ResponsiveContainer width="100%" height={Math.max(180, flavourChart.length * 28)}>
                          <BarChart data={flavourChart} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee5d6" />
                            <XAxis type="number" tickFormatter={(v) => `₹${Number(v) >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                            <YAxis type="category" dataKey="label" width={108} tick={{ fontSize: 11 }} />
                            <Tooltip
                              formatter={(value, _n, ctx) => [
                                formatInr2(value),
                                `${ctx?.payload?.quantity || 0} qty · ${ctx?.payload?.orderCount || 0} orders`
                              ]}
                              labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                            />
                            <Bar dataKey="revenue" fill="#b8860b" radius={[0, 6, 6, 0]} name="Revenue" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="dash-panel__empty">No cake flavour sales in this period.</div>
                    )}
                  </section>

                  <section className="dash-panel">
                    <div className="dash-panel__head dash-panel__head--row">
                      <div>
                        <h3>Top desserts</h3>
                        <p>Highest revenue · {insightsPeriodLabel}</p>
                      </div>
                      <Link to="/analytics?preset=7d" className="dash-panel__action">
                        Full report →
                      </Link>
                    </div>
                    {dessertChart.length ? (
                      <div className="dash-rank-chart">
                        <ResponsiveContainer width="100%" height={Math.max(180, dessertChart.length * 28)}>
                          <BarChart data={dessertChart} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee5d6" />
                            <XAxis type="number" tickFormatter={(v) => `₹${Number(v) >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                            <YAxis type="category" dataKey="label" width={108} tick={{ fontSize: 11 }} />
                            <Tooltip
                              formatter={(value, _n, ctx) => [
                                formatInr2(value),
                                `${ctx?.payload?.quantity || 0} qty · ${ctx?.payload?.orderCount || 0} orders`
                              ]}
                              labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                            />
                            <Bar dataKey="revenue" fill="#5c1520" radius={[0, 6, 6, 0]} name="Revenue" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="dash-panel__empty">No dessert sales in this period.</div>
                    )}
                  </section>
                </div>

                <section className="dash-panel">
                  <div className="dash-panel__head dash-panel__head--row">
                    <div>
                      <h3>Agent-wise sales</h3>
                      <p>Order revenue by agent · {insightsPeriodLabel}</p>
                    </div>
                    <Link to="/analytics?preset=7d" className="dash-panel__action">
                      Full report →
                    </Link>
                  </div>
                  {agentChart.length ? (
                    <div className="dash-agent-chart">
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={agentChart} margin={{ top: 8, right: 12, left: 0, bottom: 48 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee5d6" />
                          <XAxis dataKey="label" angle={-28} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => `₹${Number(v) >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                          <Tooltip
                            formatter={(value, _n, ctx) => [
                              formatInr2(value),
                              `${ctx?.payload?.orderCount || 0} orders`
                            ]}
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                          />
                          <Bar dataKey="revenue" fill="#1a1a1a" radius={[6, 6, 0, 0]} name="Revenue" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="dash-panel__empty">No agent sales in this period.</div>
                  )}
                </section>

                <section className="dash-panel dash-panel--attention">
                  <div className="dash-panel__head dash-panel__head--row">
                    <div>
                      <h3>Needs your attention</h3>
                      <p>
                        Upcoming big orders (≥ {formatInr(home.needsAttention.bigOrderMin)}) in the next 14 days
                      </p>
                    </div>
                    <Link to={ordersTodayPath} className="dash-panel__action">
                      All orders →
                    </Link>
                  </div>
                  {home.needsAttention.orders?.length ? (
                    <div className="dash-attention">
                      {home.needsAttention.orders.map((o) => (
                        <Link key={o.id} to={`/orders/${o.id}`} className="dash-attention__card">
                          <div className="dash-attention__top">
                            <strong>{o.orderNumber}</strong>
                            <span
                              className={`dash-status dash-status--${String(o.status)
                                .toLowerCase()
                                .replace(/\s+/g, '-')}`}
                            >
                              {o.status}
                            </span>
                          </div>
                          <div className="dash-attention__customer">{o.customerName}</div>
                          <div className="dash-attention__meta">
                            <span>
                              {formatDateDisplay(o.deliveryDate)}
                              {o.deliveryTime ? ` · ${o.deliveryTime}` : ''}
                            </span>
                            <span>{o.category}</span>
                          </div>
                          <div className="dash-attention__money">
                            <span>{formatInr(o.totalAmount)}</span>
                            {o.dueAmount > 0 ? (
                              <em>Due {formatInr(o.dueAmount)}</em>
                            ) : (
                              <em className="is-ok">Paid up</em>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="dash-panel__empty">
                      No large upcoming orders in the next 14 days. You’re clear on high-value deliveries.
                    </div>
                  )}
                </section>
              </>
            ) : null}
          </section>
        )}

        <div className="dashboard-section">
          <h2 className="section-title">{isAdmin ? 'Workspace' : 'Quick menu'}</h2>
          <p className="section-description">
            {isAdmin ? 'Jump into tools, catalog, and operations' : 'Jump to the area you need'}
          </p>

          <div className="dashboard-grid">
            {cards.map((card) => (
              <Link key={card.to} to={card.to} className="dashboard-card">
                <div className="card-icon" aria-hidden>
                  <AppIcon name={card.icon} size={22} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">{card.title}</h3>
                  <p className="card-description">{card.description}</p>
                </div>
                <div className="card-arrow" aria-hidden>
                  <AppIcon name="chevron" size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
